import { useEffect, useMemo, useState } from 'react';
import { Alert, LinearProgress, Pagination, Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { IDetalheRmes, IListagemRmes, RmesService } from '../../../shared/services/api/lanProducao/ListagemMensal';
import * as React from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { FerramentasDaListagemPeriodo } from '../../../shared/components/ferramentas-da-listagem/FerramentasDaListagemPeriodo';

dayjs.locale('pt-br');

interface RowProps {
  row: IListagemRmes;
  pontoData: IDetalheRmes[];
  pontoLoading: boolean;
  idSetected: string;
  pontoTotalCount: number;
  pontoPagina: number;
  busca: string;
  onExpand: (idfun: string) => void;
  onPageChange: (page: number) => void;
}

function Row({
  row,
  pontoData,
  pontoLoading,
  idSetected,
  pontoTotalCount,
  pontoPagina,
  busca,
  onExpand,
  onPageChange,
}: RowProps) {
  const [open, setOpen] = React.useState(false);

  const handleExpandClick = () => {
    if (!open) {
      onExpand(row.idfun);
    }
    setOpen((prev) => !prev);
  };

  const isSelected = idSetected === row.idfun;

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell width={50}>
          <IconButton aria-label="expand row" size="small" onClick={handleExpandClick}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {row.nome}
        </TableCell>
        <TableCell align="center" sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}>
          {(row.sum ?? 0).toFixed(2)}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Listagem Produção
              </Typography>
              {pontoLoading && isSelected ? (
                <LinearProgress variant="indeterminate" />
              ) : (
                <Table size="small" aria-label="detalhes">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Data</TableCell>
                      <TableCell align="center">Produção</TableCell>
                      <TableCell align="center">Pivô</TableCell>
                      <TableCell align="center">Valor Atividade</TableCell>
                      <TableCell align="center">Valor Adicional</TableCell>
                      <TableCell align="center">Meta</TableCell>
                      <TableCell align="center">Quantidade</TableCell>
                      <TableCell align="center">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isSelected && pontoData.length === 0 && !pontoLoading && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          Nenhum lançamento encontrado para este funcionário no período {busca}
                        </TableCell>
                      </TableRow>
                    )}
                    {isSelected &&
                      pontoData.map((pontodataRow) => (
                        <TableRow key={pontodataRow.idlanpro}>
                          <TableCell align="center">{dayjs(pontodataRow.datalan).format('DD/MM/YYYY')}</TableCell>
                          <TableCell align="center">{pontodataRow.descricao}</TableCell>
                          <TableCell align="center">{pontodataRow.pivot}</TableCell>
                          <TableCell align="center">{pontodataRow.vlatividade?.toFixed(2)}</TableCell>
                          <TableCell align="center">{pontodataRow.vladicional?.toFixed(2)}</TableCell>
                          <TableCell align="center">{pontodataRow.meta}</TableCell>
                          <TableCell align="center">{pontodataRow.qtd}</TableCell>
                          <TableCell align="center">{pontodataRow.total?.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>

                  {isSelected && pontoData.length > 0 && (
                    <TableFooter>
                      <TableRow />
                      {pontoTotalCount > 31 && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Pagination
                              page={pontoPagina}
                              count={Math.ceil(pontoTotalCount / 31)}
                              onChange={(_, newPage) => onPageChange(newPage)}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableFooter>
                  )}
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export const ListagemMensal: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const [rowss, setRowss] = useState<IListagemRmes[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [idSetected, setIdSetected] = useState('');
  const [pontoData, setPontoData] = useState<IDetalheRmes[]>([]);
  const [pontoLoading, setPontoLoading] = useState(false);
  const [pontoPagina, setPontoPagina] = useState(1);
  const [pontoTotalCount, setPontoTotalCount] = useState(0);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const handleCloseMsg = () => setOpenmsg(false);

  const busca = useMemo(() => {
    return searchParams.get('busca') || `${dayjs().format('MMMM')}/${dayjs().year()}`;
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      RmesService.getAll(pagina, busca).then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setTotalCount(result.totalCount);
          setRowss(result.data);
        }
      });
    });
  }, [busca, pagina]);

  useEffect(() => {
    if (idSetected === '') return;

    setPontoLoading(true);
    RmesService.getById(pontoPagina, idSetected, busca, idSetected)
      .then((result) => {
        setPontoLoading(false);
        if (result instanceof Error) {
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
          setPontoData([]);
        } else {
          setPontoData(result.data);
          setPontoTotalCount(result.totalCount);
        }
      })
      .catch(() => {
        setPontoLoading(false);
        setTms('error');
        setMsg('Erro ao carregar detalhes');
        setOpenmsg(true);
      });
  }, [idSetected, pontoPagina, busca]);

  useEffect(() => {
    if (idSetected !== '') setPontoPagina(1);
  }, [idSetected]);

  const handleExpand = (idfun: string) => {
    setIdSetected(idfun);
  };

  return (
    <LayoutBaseDePagina
      titulo='Listagem de Produção Mensal'
      barraDeFerramentas={
        <FerramentasDaListagemPeriodo
          mostrarBotaoNovo={false}
          mostrarInputBusca
          textoDaBusca={busca}
          aoMudarTextoDeBusca={texto => setSearchParams({ busca: texto, pagina: '1' }, { replace: true })}
        />
      }
    >
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={6000} onClose={handleCloseMsg}>
        <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
          {msg}
        </Alert>
      </Snackbar>

      <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
        <Table size='small' aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align="left">Colaborador</TableCell>
              <TableCell align="center">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <LinearProgress variant="indeterminate" />
                </TableCell>
              </TableRow>
            ) : (
              rowss.map((row) => (
                <Row
                  key={row.idfun}
                  row={row}
                  pontoData={idSetected === row.idfun ? pontoData : []}
                  pontoLoading={pontoLoading}
                  idSetected={idSetected}
                  pontoTotalCount={pontoTotalCount}
                  pontoPagina={pontoPagina}
                  busca={busca}
                  onExpand={handleExpand}
                  onPageChange={setPontoPagina}
                />
              ))
            )}
          </TableBody>

          {!isLoading && totalCount === 0 && (
            <caption>{Environment.LISTAGEM_VAZIA}</caption>
          )}

          <TableFooter>
            {!isLoading && totalCount > 0 && totalCount > Environment.LIMITE_DE_LINHAS && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Pagination
                    page={pagina}
                    count={Math.ceil(totalCount / Environment.LIMITE_DE_LINHAS)}
                    onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableFooter>
        </Table>
      </TableContainer>
    </LayoutBaseDePagina>
  );
};